import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import Button from '../../shared/button.component';
import Select from '../../shared/reactSelect/select.component';
import Spinner from '../../shared/spinner.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import { withPermissions, permissions } from '../../shared/authorization';
import ModalNames from './modalNames';
import { modalDataSelector } from '../../shared/modals/modals.selectors';
import AddChartAccountModal from './addChartAccountModal.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import HamburgerDropdown from './hamburgerDropdown.component';
import DeleteChartModal from './deleteChartModal.component';
import { isInCurrentPeriodSelector, environmentSuffixSelector } from '../../shared/store/selectors';
import NoDataToDisplay from '../../shared/noDataToDisplay.component';
import { NO_FLAG } from '../../shared/constants';
import AddEditChartModal from './addEditChartModal.component';

import {
  fetchInitialDataForChartOfAccounts,
  selectPeriodForChartOfAccounts,
  selectChartForChartOfAccounts,
  showChartAccountModalForAddEdit,
} from './store/actions';
import {
  selectFetchChartAccountsErrorSelector,
  selectFetchChartAccountsLoadingSelector,
} from './store/selectors';
import { hideDecimalsSelector, negativeMinusSelector, displayZeroSelector } from '../../userData/store/selectors';

import { filterSelectFactory } from '../../utils/dropdownsHelpers';
import ChartOfAccountsGrid from './chartOfAccountsGrid.component';
import { ChartMasterPropTypes, ChartAccountsPropTypes, ChartPropTypes } from './propTypes';
import DeleteChartAccountModal from './deleteChartAccountModal.component';
import ImportChartModal from './importChartModal.component';
import downloadChartXlsx from './downloadChartXlsx';

const filterSelectDefault = filterSelectFactory();

class ChartOfAccountsPage extends PureComponent {
  static propTypes = {
    canModify: PropTypes.bool.isRequired,
    canModifyInSelectedPeriod: PropTypes.bool.isRequired,
    canModifyByAssignment: PropTypes.bool.isRequired,
    isInCurrentPeriod: PropTypes.bool.isRequired,

    periods: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
    })).isRequired,

    chart: ChartMasterPropTypes,
    chartAccounts: ChartAccountsPropTypes,

    fetchInitialDataForChartOfAccounts: PropTypes.func.isRequired,
    selectPeriodForChartOfAccounts: PropTypes.func.isRequired,
    selectChartForChartOfAccounts: PropTypes.func.isRequired,

    addEditChartModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: ChartPropTypes,
    }),

    importChartModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
    }),

    deleteChartModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
    }),

    showChartAccountModalForAddEdit: PropTypes.func.isRequired,
    addEditChartAccountModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        localAccount: PropTypes.string.isRequired,
      }),
    }),

    deleteChartAccountModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        localAccount: PropTypes.string.isRequired,
        localAccountDescription: PropTypes.string.isRequired,
      }),
    }),

    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
      taxYear: PropTypes.string.isRequired,
    }),

    chartNames: PropTypes.arrayOf(PropTypes.shape({
      chartName: PropTypes.string.isRequired,
    })),
    chartName: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
    fetchChartAccountsError: PropTypes.string.isRequired,
    chartAccountsLoading: PropTypes.bool.isRequired,

    shouldDisplayZero: PropTypes.bool.isRequired,
    hideDecimals: PropTypes.bool.isRequired,
    negativeMinus: PropTypes.bool.isRequired,
    environmentSuffix: PropTypes.string.isRequired,
  };

  componentDidMount() {
    const { period, chartName, canModify, fetchInitialDataForChartOfAccounts } = this.props;

    fetchInitialDataForChartOfAccounts(period, chartName, canModify);
  }

  selectPeriod = selectedPeriod => {
    const { periods, chartName, selectPeriodForChartOfAccounts, canModify } = this.props;

    const value = selectedPeriod ? selectedPeriod.value : null;
    selectPeriodForChartOfAccounts(periods.find(period => period.name === value), chartName, canModify);
  };

  selectChartName = selectedChartName => {
    const { period, selectChartForChartOfAccounts } = this.props;
    selectChartForChartOfAccounts(period, selectedChartName);
  };

  showChartAccountModalForAdd = () => {
    this.props.showChartAccountModalForAddEdit();
  };

  downloadChart = callback => {
    const {
      period,
      chartName,
      chartAccounts,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      environmentSuffix,
    } = this.props;

    downloadChartXlsx({
      periodName: period.name,
      chartName: chartName.label,
      chartAccounts,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      environmentSuffix,
    });

    if (callback) {
      callback();
    }
  };

  render() {
    const {
      canModify,
      canModifyInSelectedPeriod,
      isInCurrentPeriod,
      canModifyByAssignment,
      period,
      chartNames,
      chartName,
      fetchChartAccountsError,
      chart,
      chartAccounts,
      chartAccountsLoading,
      addEditChartModalData,
      deleteChartModalData,
      addEditChartAccountModalData,
      deleteChartAccountModalData,
      importChartModalData,
    } = this.props;

    const selectedChartName = chartName ? chartName.value : '';

    const chartNamesForSelect = chartNames.map(({ chartName }) => ({
      value: chartName,
      label: chartName,
    }));

    const canModifyChartAccountsPrivilege = canModifyInSelectedPeriod
      || (canModifyByAssignment && isInCurrentPeriod);

    const importHasErrors = !chart || chart.importStatus === -1;
    const importResultsMsg = !chart ? '' : chart.importResults;
    const isTpcGenerated = !chart || chart.tpcGenerated === NO_FLAG;

    return (
      <div className="chart-of-accounts-page">
        <SecondaryMenu>
          <PeriodDropdown
            onChange={this.selectPeriod}
          />
          <Select
            className="select-company"
            clearable={false}
            filterOptions={filterSelectDefault}
            name="chartName"
            options={chartNamesForSelect}
            onChange={this.selectChartName}
            scrollMenuIntoView={false}
            value={selectedChartName}
            virtualized
          />
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown
              period={period}
              chartName={chartName}
              canModify={canModifyInSelectedPeriod}
              isTpcGenerated={isTpcGenerated}
              downloadChart={this.downloadChart}
              disabledActions={{
                chartNotSelected: !chartName || chartName.value.trim() === '-',
              }}
            />
          </div>
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid">
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Chart of Accounts</h5>
                </div>
              </div>
            </div>
            <div className="row no-gutters page-content-container">
              <div className="col">
                {!selectedChartName && <NoDataToDisplay header="No Chart Selected" details="Please select a chart to view." />}
                {selectedChartName && fetchChartAccountsError && <NoDataToDisplay header={fetchChartAccountsError} />}
                {chartAccountsLoading ? <Spinner />
                  : selectedChartName && !fetchChartAccountsError
                  && <ChartOfAccountsGrid
                    componentToRenderOnSearchBar={
                      selectedChartName && canModifyChartAccountsPrivilege && isTpcGenerated
                        ? (
                          <Button
                            type="button"
                            kind="primary"
                            className="a-my-10"
                            onClick={this.showChartAccountModalForAdd}
                          >
                            Add Account
                          </Button>
                        ) : null
                    }
                    messageToRenderOnSearchBar={(
                      <div className="d-flex align-items-center justify-content-center">
                        {importHasErrors ? <div className="text-danger">{importResultsMsg}</div> : <div>{importResultsMsg}</div>}
                      </div>
                    )}
                    shouldDisplayAdminColumn={canModifyChartAccountsPrivilege && isTpcGenerated}
                    chartAccounts={chartAccounts}
                    chartAccountsLoading={chartAccountsLoading}
                  />
                }
              </div>
            </div>
          </div>
        </div>
        <DeleteChartModal
          period={period}
          entityToRemove={chartName}
          canModify={canModify}
          isVisible={deleteChartModalData.show}
        />
        <AddEditChartModal
          period={period}
          entityToEdit={addEditChartModalData && addEditChartModalData.data}
          openForEdit={Boolean(addEditChartModalData && addEditChartModalData.data)}
          isVisible={addEditChartModalData.show}
        />

        <AddChartAccountModal
          period={period}
          chartName={chartName}
          entityToEdit={addEditChartAccountModalData.data}
          openForEdit={Boolean(addEditChartAccountModalData && addEditChartAccountModalData.data)}
          isVisible={addEditChartAccountModalData.show}
        />

        <ImportChartModal
          period={period}
          chartName={chartName}
          isVisible={importChartModalData.show}
        />
        <DeleteChartAccountModal
          isVisible={deleteChartAccountModalData.show}
          period={period}
          chartName={chartName}
          entityToRemove={deleteChartAccountModalData.data}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { shared, administration } = state;

  return {
    canModifyByAssignment: administration.chartOfAccounts.canModifyByAssignment,
    isInCurrentPeriod: isInCurrentPeriodSelector(state),
    period: shared.period,
    periods: shared.periods,
    chart: administration.chartOfAccounts.chart,
    chartAccounts: administration.chartOfAccounts.chartAccounts,
    chartNames: administration.chartOfAccounts.chartNames,
    chartName: administration.chartOfAccounts.chartName,
    chartAccountsLoading: selectFetchChartAccountsLoadingSelector(state),
    fetchChartAccountsError: selectFetchChartAccountsErrorSelector(state),
    addEditChartModalData: modalDataSelector(state, ModalNames.ChartAddEdit),
    importChartModalData: modalDataSelector(state, ModalNames.ChartImport),
    addEditChartAccountModalData: modalDataSelector(state, ModalNames.ChartAccountAddEdit),
    deleteChartModalData: modalDataSelector(state, ModalNames.ChartDelete),
    deleteChartAccountModalData: modalDataSelector(state, ModalNames.ChartAccountDelete),
    deleteModalData: modalDataSelector(state, ModalNames.ChartAccountDelete),
    shouldDisplayZero: displayZeroSelector(state),
    hideDecimals: hideDecimalsSelector(state),
    negativeMinus: negativeMinusSelector(state),
    environmentSuffix: environmentSuffixSelector(state),
  };
}

export default withPermissions(permissions.CHART_OF_ACCOUNTS)(connect(
  mapStateToProps,
  {
    fetchInitialDataForChartOfAccounts,
    selectPeriodForChartOfAccounts,
    selectChartForChartOfAccounts,
    showChartAccountModalForAddEdit,
  }
)(ChartOfAccountsPage));
